import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const BPRSList = ({ assessments = [], loading, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredRecords = assessments.filter((r) =>
        Object.values(r).some(
            (v) => v != null && String(v).toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This BPRS assessment will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                onDelete(id);
                Swal.fire("Deleted!", "BPRS assessment deleted successfully.", "success");
            }
        });
    };

    const handleExportPDF = () => {
        const doc = new jsPDF("l", "mm", "a4");
        doc.text("Brief Psychiatric Rating Scale (BPRS) Assessments", 14, 15);

        const tableColumn = ["#", "Client Name", "Assessment Date", "Total Score", "Assessed By"];
        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.client_name || "",
            r.assessment_date || "",
            r.total_score || 0,
            r.assessed_by || ""
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 25,
            theme: "grid",
            styles: { fontSize: 9 },
        });

        doc.save("bprs_assessments.pdf");
    };

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Rows per page:</span>

                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
                    >
                        {[5, 10, 15, 20].map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        { name: "S.No", cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage, width: "80px" },
        { name: "Client Name", selector: (row) => row.client_name || "", sortable: true },
        { name: "Assessment Date", selector: (row) => row.assessment_date || "", sortable: true },
        { name: "Total Score", selector: (row) => row.total_score || 0, sortable: true, center: true },
        { name: "Assessed By", selector: (row) => row.assessed_by || "", sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(row.id)}>
                        <i className="ri-edit-line"></i>
                    </button>

                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                        <i className="ri-delete-bin-line"></i>
                    </button>
                </div>
            ),
            width: "130px",
        },
    ];

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>

                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>

                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="bprs_assessments.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search BPRS assessments..."
                        className="form-control form-control-sm"
                        style={{ width: "300px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-muted flex-grow-1 text-center">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    <button className="btn btn-sm btn-primary" onClick={onAdd}>
                        <i className="ri-add-line me-1"></i> Add New
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={filteredRecords}
                progressPending={loading}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={totalRows}
                onChangeRowsPerPage={setRowsPerPage}
                onChangePage={setCurrentPage}
                paginationComponent={CustomPagination}
                highlightOnHover
                striped
                responsive
                noDataComponent={
                    <div className="p-5 text-center text-muted">
                        <i className="ri-brain-line fs-1 d-block mb-3"></i>
                        No BPRS assessments found
                    </div>
                }
            />
        </div>
    );
};

export default BPRSList;
